import { useState, useCallback } from 'react'
import { calendlyService } from '../../../../../services/calendlyService'
import type { CalendlyWindow, UserInfo, CalendlyUrlResponse } from '../types'

declare global {
  interface Window extends CalendlyWindow {}
}

export const useCalendlyIntegration = () => {
  const [isCalendlyLoaded, setIsCalendlyLoaded] = useState(false)
  const [calendlyUrl, setCalendlyUrl] = useState('')

  const loadCalendlyScript = useCallback((): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      if (window.Calendly) {
        resolve()
        return
      }

      // Load CSS
      const cssLink = document.createElement('link')
      cssLink.href = 'https://assets.calendly.com/assets/external/widget.css'
      cssLink.rel = 'stylesheet'
      document.head.appendChild(cssLink)

      // Load JS
      const script = document.createElement('script')
      script.src = 'https://assets.calendly.com/assets/external/widget.js'
      script.async = true
      script.onload = () => {
        setIsCalendlyLoaded(true)
        resolve()
      }
      script.onerror = () => {
        reject(new Error('Failed to load Calendly script'))
      }
      document.head.appendChild(script)
    })
  }, [])

  const initCalendlyPopupWithPrefill = useCallback(
    async (url: string, userData: UserInfo) => {
      try {
        console.log('🚀 Iniciando popup de Calendly con prefill:', {
          url,
          userData,
        })

        await loadCalendlyScript()
        console.log('✅ Script de Calendly cargado')

        if (window.Calendly) {
          console.log('✅ Calendly disponible, abriendo popup con prefill')

          // Cleanup previous popups
          try {
            window.Calendly.closePopupWidget()
          } catch {
            // Ignore cleanup errors
          }

          // Open popup with prefill according to documentation
          window.Calendly.initPopupWidget({
            url: url,
            prefill: {
              name: userData.name,
              email: userData.email,
              customAnswers: {
                a1: userData.primaryAddress || 'Dirección no especificada',
                a2: userData.phone || '',
                a3: userData.userId || '',
                a4: 'OLR Leasing',
                a5: `Dirección para la visita: ${userData.primaryAddress || 'No especificada'}\nTeléfono de contacto: ${userData.phone || 'No especificado'}\nUsuario ID: ${userData.userId || 'No especificado'}`,
              },
            },
            utm: {
              utmSource: 'olr_platform',
              utmMedium: 'onboarding',
              utmCampaign: 'schedule_visit',
              utmContent: 'registration_flow',
              utmTerm: userData.userId || '',
            },
          })

          console.log('✅ Popup de Calendly abierto exitosamente con prefill')
        } else {
          console.error('❌ Error: Calendly no disponible')
          throw new Error('Calendly no está disponible')
        }
      } catch (error) {
        console.error('❌ Error abriendo popup de Calendly:', error)
        throw error
      }
    },
    [loadCalendlyScript]
  )

  const generateCalendlyUrl = useCallback(
    async (
      userInfo: UserInfo,
      appointmentType: string = 'visita_ocular'
    ): Promise<string> => {
      const finalUserName =
        userInfo.name && userInfo.name.trim() !== ''
          ? userInfo.name
          : 'Usuario OLR'

      console.log('Datos para Calendly:', {
        email: userInfo.email,
        name: finalUserName,
        phone: userInfo.phone,
        address: userInfo.primaryAddress,
        userId: userInfo.userId,
      })

      const response: CalendlyUrlResponse =
        await calendlyService.generateCalendlyUrl({
          email: userInfo.email,
          name: finalUserName,
          phone: userInfo.phone,
          address: userInfo.primaryAddress,
          userId: userInfo.userId,
          appointmentType: appointmentType,
          notes: `${
            appointmentType === 'firma_contrato'
              ? 'Cita para firma de contrato - OLR Leasing'
              : 'Visita de verificación para proceso de onboarding - OLR Leasing'
          }
        
Dirección para la visita: ${userInfo.primaryAddress || 'No especificada'}
Teléfono de contacto: ${userInfo.phone || 'No especificado'}
ID de usuario: ${userInfo.userId || 'No especificado'}`,
          companyName: 'OLR Leasing',
          utmSource: 'olr_platform',
          utmMedium: 'onboarding',
          utmCampaign:
            appointmentType === 'firma_contrato'
              ? 'contract_signing'
              : 'schedule_visit',
        })

      console.log('Respuesta completa de Calendly:', response)

      // Extract URL trying different response structures
      let calendlyUrl = null

      if (response?.data?.data?.url) {
        calendlyUrl = response.data.data.url
        console.log('URL encontrada en response.data.data.url')
      } else if (response?.data?.url) {
        calendlyUrl = response.data.url
        console.log('URL encontrada en response.data.url')
      } else if (response?.url) {
        calendlyUrl = response.url
        console.log('URL encontrada en response.url')
      }

      if (!calendlyUrl || calendlyUrl.trim() === '') {
        console.error(
          'URL vacía. Response completa:',
          JSON.stringify(response, null, 2)
        )
        throw new Error(
          `La URL de Calendly está vacía. Estructura recibida: ${JSON.stringify(response)}`
        )
      }

      setCalendlyUrl(calendlyUrl)
      return calendlyUrl
    },
    []
  )

  const cleanupCalendly = useCallback(() => {
    if (window.Calendly) {
      try {
        window.Calendly.closePopupWidget()
      } catch {
        console.log('No hay popup para cerrar')
      }
    }
  }, [])

  return {
    isCalendlyLoaded,
    calendlyUrl,
    loadCalendlyScript,
    initCalendlyPopupWithPrefill,
    generateCalendlyUrl,
    cleanupCalendly,
    setCalendlyUrl,
  }
}
